import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Typography,
  Stack
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Modal from '@components/Modal/Modal';
import { useAddWorkOrderCharge } from '@api/enterprise/workorder';
import { useServiceAgreementChargeTypesData } from '../../hooks/useServiceAgreementChargeTypesData';
import { formatCurrency } from '@utils/numberFormatters';
import type { ServiceAgreementChargeType } from '../../types/serviceAgreementChargeType.types';

type Props = {
  open: boolean;
  onClose: () => void;
  jobNumber: string;
};

export const AddChargeModal = ({ open, onClose, jobNumber }: Props) => {
  const { t } = useTranslation('charges');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedChargeType, setSelectedChargeType] =
    useState<ServiceAgreementChargeType | null>(null);
  const [quantity, setQuantity] = useState<number>(1);

  const { mutate: addCharge, isPending: isAddingCharge } =
    useAddWorkOrderCharge(onClose);

  const { groups, getTypesByGroup, isLoading, error } =
    useServiceAgreementChargeTypesData(open);

  const availableTypes = useMemo(() => {
    if (!selectedGroup) return [];
    return getTypesByGroup(selectedGroup);
  }, [selectedGroup, getTypesByGroup]);

  const totalCost = useMemo(() => {
    if (!selectedChargeType?.charge || !quantity) return 0;
    return selectedChargeType.charge * quantity;
  }, [selectedChargeType, quantity]);

  useEffect(() => {
    if (!open) {
      setSelectedGroup('');
      setSelectedChargeType(null);
      setQuantity(1);
    }
  }, [open]);

  const handleGroupChange = (group: string) => {
    setSelectedGroup(group);

    // Get the types for this group and find the default
    const typesForGroup = getTypesByGroup(group);
    const defaultType = typesForGroup.find(t => t.isDefault === '1');

    // If there's a default, select it; otherwise clear selection
    if (defaultType) {
      setSelectedChargeType(defaultType);
    } else {
      setSelectedChargeType(null);
    }
  };

  const handleTypeChange = (typeName: string) => {
    const selected = availableTypes.find(t => t.name === typeName);
    setSelectedChargeType(selected || null);
  };

  const handleAddCharge = () => {
    if (!selectedChargeType) return;

    addCharge({
      jobNumber,
      chargeData: {
        chargeType: selectedChargeType.type || undefined,
        lineDescription: selectedChargeType.description || undefined,
        lineQuantity: quantity,
        lineUnit: selectedChargeType.unitOfMeasure || undefined,
        lineCost: selectedChargeType.charge || undefined
      }
    });
  };

  const handleClose = () => {
    if (!isAddingCharge) {
      onClose();
    }
  };

  const isFormValid = selectedGroup && selectedChargeType && quantity > 0;

  return (
    <Modal open={open} onClose={handleClose} maxWidth="md" width="700px">
      <Modal.Header title={t('addChargeModal.title')} onClose={handleClose} />
      <Modal.Content>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('addChargeModal.errorLoading')}
          </Alert>
        ) : (
          <Stack spacing={3} sx={{ minHeight: 300 }}>
            <TextField
              select
              label="Service Charge"
              value={selectedGroup}
              onChange={e => handleGroupChange(e.target.value)}
              fullWidth
              disabled={isAddingCharge}
            >
              {groups.map(group => (
                <MenuItem key={group} value={group}>
                  {group}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Charge Type"
              value={selectedChargeType?.name || ''}
              onChange={e => handleTypeChange(e.target.value)}
              fullWidth
              disabled={!selectedGroup || isAddingCharge}
              helperText={
                !selectedGroup ? 'Please select a service charge first' : ''
              }
            >
              {availableTypes.map(type => (
                <MenuItem key={type.name} value={type.name || ''}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      alignItems: 'center'
                    }}
                  >
                    <Box>
                      <Typography variant="body2">{type.name}</Typography>
                      {type.description && (
                        <Typography variant="caption" color="text.secondary">
                          {type.description}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ ml: 2, fontWeight: 500 }}>
                      {formatCurrency(type.charge)}
                      {type.unitOfMeasure && `/${type.unitOfMeasure}`}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            {selectedChargeType && (
              <>
                <TextField
                  label={`Quantity${selectedChargeType.unitOfMeasure ? ` (${selectedChargeType.unitOfMeasure})` : ''}`}
                  value={quantity}
                  onChange={e => {
                    const value = e.target.value;
                    // Allow empty string for clearing the field
                    if (value === '') {
                      setQuantity(0);
                    } else {
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue)) {
                        setQuantity(numValue);
                      }
                    }
                  }}
                  fullWidth
                  disabled={isAddingCharge}
                  inputProps={{
                    inputMode: 'decimal',
                    pattern: '[0-9]*\\.?[0-9]*',
                    min: 0.01,
                    step: 0.01
                  }}
                />

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.03)',
                    borderRadius: 1
                  }}
                >
                  <Stack spacing={1}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Unit Cost
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(selectedChargeType.charge)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Quantity
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {quantity} {selectedChargeType.unitOfMeasure || ''}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pt: 1,
                        borderTop: 1,
                        borderColor: 'divider'
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Total Cost
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, color: 'primary.main' }}
                      >
                        {formatCurrency(totalCost)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
        )}
      </Modal.Content>
      <Modal.Footer
        primaryButton={{
          text: isAddingCharge
            ? t('addChargeModal.adding')
            : t('addChargeModal.add'),
          onClick: handleAddCharge,
          disabled: !isFormValid || isAddingCharge,
          loading: isAddingCharge
        }}
        secondaryButton={{
          text: t('common:buttons.cancel'),
          onClick: handleClose,
          disabled: isAddingCharge
        }}
      />
    </Modal>
  );
};
